import React, { useEffect, useState, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { whatsappService, WhatsAppConnection, WahaSessionStatus } from '../services/whatsappService';
import { supabase } from '../../../integrations/supabase/client';
import { Button } from '../../../components/ui/button';
import { Trash2, Loader2, RefreshCw, Plug } from 'lucide-react';
import { toast } from '../../../hooks/use-toast';
import { ReconnectModal } from './ReconnectModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';

interface ChannelWithAgent extends WhatsAppConnection {
    assigned_agent_name: string | null;
}

const isWhatsAppChannel = (nombreInstancia: string | null): boolean => {
    if (!nombreInstancia) return true;
    return nombreInstancia.toLowerCase().startsWith('maxi');
};

const WhatsAppIcon = () => (
    <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

const GHLIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
        <rect width="24" height="24" rx="4" fill="#FF6B35"/>
        <path d="M7 8h10M7 12h6M7 16h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

type RowLiveStatus = WahaSessionStatus | 'UNKNOWN';

const LIVE_BADGE: Record<WahaSessionStatus, { label: string; cls: string; dot: string; pulse?: boolean }> = {
    WORKING: { label: 'Conectado', cls: 'bg-green-100 text-green-700', dot: 'bg-green-500', pulse: true },
    SCAN_QR_CODE: { label: 'Escanear QR', cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', pulse: true },
    STARTING: { label: 'Iniciando', cls: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', pulse: true },
    STOPPED: { label: 'Detenido', cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
    FAILED: { label: 'Fallido', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

const Badge: React.FC<{ cls: string; dot: string; label: string; pulse?: boolean }> = ({ cls, dot, label, pulse }) => (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dot} ${pulse ? 'animate-pulse' : ''}`} />
        {label}
    </span>
);

// Fallback badge basado en el estado guardado en BD (canales no-WAHA o estado vivo desconocido)
const EstadoBadge: React.FC<{ estado: string }> = ({ estado }) => {
    if (estado === 'connected') return <Badge cls="bg-green-100 text-green-700" dot="bg-green-500" pulse label="Conectado" />;
    if (estado === 'disconnected') return <Badge cls="bg-gray-100 text-gray-600" dot="bg-gray-400" label="Desconectado" />;
    if (estado === 'pending') return <Badge cls="bg-yellow-100 text-yellow-700" dot="bg-yellow-500" pulse label="Pendiente" />;
    return <Badge cls="bg-red-100 text-red-700" dot="bg-red-500" label="Error" />;
};

// Prefiere el estado vivo de WAHA; si es UNKNOWN o aún no cargó, cae al estado de BD
const StatusBadge: React.FC<{ live?: RowLiveStatus; estado: string }> = ({ live, estado }) => {
    if (live && live !== 'UNKNOWN') {
        const b = LIVE_BADGE[live];
        return <Badge cls={b.cls} dot={b.dot} label={b.label} pulse={b.pulse} />;
    }
    return <EstadoBadge estado={estado} />;
};

const ChannelsTable: React.FC = () => {
    const [channels, setChannels] = useState<ChannelWithAgent[]>([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [channelToDelete, setChannelToDelete] = useState<ChannelWithAgent | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [checkingInUse, setCheckingInUse] = useState<string | null>(null);
    const [liveStatus, setLiveStatus] = useState<Record<string, RowLiveStatus>>({});
    const [statusLoading, setStatusLoading] = useState(false);
    const [reconnectTarget, setReconnectTarget] = useState<ChannelWithAgent | null>(null);

    useEffect(() => {
        const loadChannels = async () => {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) {
                    console.error('Usuario no autenticado');
                    return;
                }
                const connections = await whatsappService.getConnections(user.id);

                // Fetch agent assignments for each channel via agent_channels → agents
                const phones = connections.map(c => c.numero_telefono);
                const { data: assignments } = await supabase
                    .from('agent_channels')
                    .select('numero_telefono, agents ( nombre )')
                    .in('numero_telefono', phones);

                const agentMap: Record<string, string> = {};
                (assignments || []).forEach((a: any) => {
                    if (a.agents?.nombre) {
                        agentMap[a.numero_telefono] = a.agents.nombre;
                    }
                });

                const channelsWithAgents: ChannelWithAgent[] = connections.map(c => ({
                    ...c,
                    assigned_agent_name: agentMap[c.numero_telefono] || null,
                }));

                setChannels(channelsWithAgents);
            } catch (error) {
                console.error('Error cargando canales:', error);
            } finally {
                setLoading(false);
            }
        };

        loadChannels();
    }, []);

    // Consulta el estado vivo de WAHA en paralelo (solo canales WhatsApp). No bloquea el render inicial.
    const refreshLiveStatus = useCallback(async (rows: ChannelWithAgent[]) => {
        const waha = rows.filter(r => isWhatsAppChannel(r.nombre_instancia));
        if (waha.length === 0) return;
        setStatusLoading(true);
        try {
            const results = await Promise.allSettled(
                waha.map(r => whatsappService.getSessionInfo(r.nombre_instancia))
            );
            setLiveStatus(prev => {
                const next = { ...prev };
                waha.forEach((r, i) => {
                    const res = results[i];
                    next[r.id] = res.status === 'fulfilled' ? res.value.status : 'UNKNOWN';
                });
                return next;
            });
        } finally {
            setStatusLoading(false);
        }
    }, []);

    // Cargar estado en vivo después del primer render (cuando termina la carga de BD)
    useEffect(() => {
        if (!loading && channels.length > 0) {
            void refreshLiveStatus(channels);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const handleReconnected = (id: string, status: WahaSessionStatus, phone: string) => {
        setLiveStatus(prev => ({ ...prev, [id]: status }));
        setChannels(prev => prev.map(ch => ch.id === id
            ? { ...ch, estado: 'connected', numero_telefono: phone || ch.numero_telefono }
            : ch));
        setReconnectTarget(null);
    };

    const needsReconnect = (c: ChannelWithAgent): boolean => {
        if (!isWhatsAppChannel(c.nombre_instancia)) return false;
        const live = liveStatus[c.id];
        if (live === 'WORKING' || live === 'STARTING') return false;
        if (live === 'FAILED' || live === 'STOPPED' || live === 'SCAN_QR_CODE') return true;
        // UNKNOWN o aún sin cargar → caer al estado de BD
        return c.estado === 'disconnected' || c.estado === 'error';
    };

    const checkChannelInUse = async (numeroTelefono: string): Promise<boolean> => {
        try {
            const { data, error } = await supabase
                .from('agent_channels')
                .select('id')
                .eq('numero_telefono', numeroTelefono)
                .limit(1);

            if (error) throw error;
            return (data?.length ?? 0) > 0;
        } catch (error) {
            console.error('Error verificando uso del canal:', error);
            return false;
        }
    };

    const handleDeleteClick = async (channel: ChannelWithAgent) => {
        setCheckingInUse(channel.id);
        try {
            const inUse = await checkChannelInUse(channel.numero_telefono);

            if (inUse) {
                toast.error("Este canal está siendo utilizado por uno o más agentes. Desconéctalo de los agentes antes de eliminarlo.");
                return;
            }

            setChannelToDelete(channel);
            setShowDeleteDialog(true);
        } finally {
            setCheckingInUse(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!channelToDelete || deletingId) return;

        setDeletingId(channelToDelete.id);
        try {
            await whatsappService.deleteConnection(channelToDelete.id, channelToDelete.nombre_instancia);

            // Verificar que realmente se eliminó
            const { data } = await supabase
                .from('whatsapp_connections')
                .select('id')
                .eq('id', channelToDelete.id)
                .maybeSingle();

            if (data) {
                console.error('[Channels] El registro no se eliminó de la BD');
                toast.error("No se pudo eliminar el canal. Intenta de nuevo.");
                return;
            }

            setChannels(prev => prev.filter(ch => ch.id !== channelToDelete.id));
            toast.success("El canal se ha eliminado correctamente.");

        } catch (error) {
            console.error('Error eliminando canal:', error);
            toast.error("No se pudo eliminar el canal. Por favor, intenta de nuevo.");
        } finally {
            setDeletingId(null);
            setShowDeleteDialog(false);
            setChannelToDelete(null);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow p-8 flex items-center justify-center min-h-[40vh]">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-maxi-text-light text-sm">Cargando canales...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-maxi-text-dark">Canales conectados</h2>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => refreshLiveStatus(channels)}
                    disabled={statusLoading}
                >
                    {statusLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Actualizar estado
                </Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow className='h-[6vh]'>
                        <TableHead className="text-maxi-text-dark font-semibold w-[12%]">Canal</TableHead>
                        <TableHead className="text-maxi-text-dark font-semibold w-[16%]">Número</TableHead>
                        <TableHead className="text-maxi-text-dark font-semibold w-[22%]">Nombre de Instancia</TableHead>
                        <TableHead className="text-maxi-text-dark font-semibold w-[22%]">Agente Asignado</TableHead>
                        <TableHead className="text-maxi-text-dark font-semibold w-[16%]">Estado</TableHead>
                        <TableHead className="text-maxi-text-dark font-semibold w-[12%] text-right">Acciones</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {channels.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} className="text-center text-maxi-text-light py-8">
                                No hay canales conectados
                            </TableCell>
                        </TableRow>
                    ) : (
                        channels.map((channel) => (
                            <TableRow key={channel.id} className="h-[6vh] hover:bg-gray-50 transition-colors">
                                <TableCell>
                                    {isWhatsAppChannel(channel.nombre_instancia) ? (
                                        <span className="inline-flex items-center gap-2 text-maxi-text-dark">
                                            <WhatsAppIcon />
                                            WhatsApp
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-2 text-maxi-text-dark">
                                            <GHLIcon />
                                            Go High Level
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-maxi-text-dark font-mono text-sm">{channel.numero_telefono}</TableCell>
                                <TableCell className="text-maxi-text-dark text-sm">{channel.nombre_instancia}</TableCell>
                                <TableCell className="text-sm">
                                    {channel.assigned_agent_name ? (
                                        <span className="text-maxi-text-dark font-medium">{channel.assigned_agent_name}</span>
                                    ) : (
                                        <span className="text-gray-400 italic">Sin asignar</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <StatusBadge
                                        live={isWhatsAppChannel(channel.nombre_instancia) ? liveStatus[channel.id] : undefined}
                                        estado={channel.estado}
                                    />
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {needsReconnect(channel) && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setReconnectTarget(channel)}
                                                disabled={deletingId === channel.id}
                                                title="Reconectar / generar QR"
                                                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                            >
                                                <Plug className="h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteClick(channel)}
                                            disabled={deletingId === channel.id || checkingInUse === channel.id}
                                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            {deletingId === channel.id || checkingInUse === channel.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { if (!deletingId) setShowDeleteDialog(open); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar canal?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Esta acción eliminará permanentemente el canal de WhatsApp{' '}
                            <strong>{channelToDelete?.numero_telefono}</strong> y su sesión de WhatsApp.
                            Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowDeleteDialog(false)} disabled={!!deletingId}>
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={!!deletingId}
                        >
                            {deletingId ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Eliminando...
                                </>
                            ) : 'Eliminar'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {reconnectTarget && (
                <ReconnectModal
                    open={!!reconnectTarget}
                    onClose={() => setReconnectTarget(null)}
                    instanceName={reconnectTarget.nombre_instancia}
                    connectionId={reconnectTarget.id}
                    currentPhone={reconnectTarget.numero_telefono}
                    onReconnected={handleReconnected}
                />
            )}
        </div>
    );
};

export default ChannelsTable;
